import React, { useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomNavbar from "../CustomPermissions/CustomNavbar";
import { API_BASE_URL } from "../../config/api";

const DatabaseBackup = () => {
    const [isBackingUp, setIsBackingUp] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    const handleBackup = async () => {
        setIsBackingUp(true);
        try {
            const accessToken = localStorage.getItem("access_token");
            const response = await axios.get(`${API_BASE_URL}/backup-database/`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                responseType: "blob", // Important for file download
            });

            // Create a blob link to download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "database_backup.json");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);

            toast.success("Database backup downloaded successfully!");
        } catch (error) {
            console.error(error);
            toast.error("Failed to generate database backup. Check permissions.");
        } finally {
            setIsBackingUp(false);
        }
    };

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleRestore = async () => {
        if (!selectedFile) {
            toast.warning("Please select a backup JSON file first.");
            return;
        }

        if (!window.confirm("WARNING: This will overwrite your current database. Are you sure you want to proceed?")) {
            return;
        }

        setIsRestoring(true);
        const formData = new FormData();
        formData.append("backup_file", selectedFile);

        try {
            const accessToken = localStorage.getItem("access_token");
            await axios.post(`${API_BASE_URL}/restore-database/`, formData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            toast.success("Database restored successfully!");
            setSelectedFile(null);
            // Clear the file input
            document.getElementById("backup-file-input").value = "";
        } catch (error) {
            console.error(error);
            toast.error("Failed to restore database from backup.");
        } finally {
            setIsRestoring(false);
        }
    };

    return (
        <div style={{ display: "flex" }}>
            <CustomNavbar />
            <div className="flex-1 p-6 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
                <ToastContainer />
                <div className="max-w-4xl mx-auto mt-10 bg-white p-8 rounded-lg shadow-lg">
                    <div className="mb-6 border-b pb-4">
                        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4zm0 5c0 2.21 3.582 4 8 4s8-1.79 8-4"></path>
                            </svg>
                            Database Backup & Restore
                        </h1>
                        <p className="text-gray-600 mt-2">Manage your complete system configuration and data.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                        {/* Backup Section */}
                        <div className="border border-blue-200 rounded-xl p-6 bg-blue-50 hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-3 bg-blue-100 rounded-full">
                                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                                    </svg>
                                </div>
                                <h2 className="text-xl font-bold text-blue-900">Backup Database</h2>
                            </div>
                            <p className="text-gray-700 mb-6 text-sm">Download a complete snapshot of your database in JSON format. Keep this file safe for future restorations.</p>

                            <button
                                onClick={handleBackup}
                                disabled={isBackingUp}
                                className={`w-full py-3 px-4 rounded-lg font-bold text-white shadow-md transition duration-200 flex justify-center items-center gap-2 ${isBackingUp ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 active:transform active:scale-95"
                                    }`}
                            >
                                {isBackingUp ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Generating Backup...
                                    </>
                                ) : (
                                    "Download Full Backup"
                                )}
                            </button>
                        </div>

                        {/* Restore Section */}
                        <div className="border border-red-200 rounded-xl p-6 bg-red-50 hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-3 bg-red-100 rounded-full">
                                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                                    </svg>
                                </div>
                                <h2 className="text-xl font-bold text-red-900">Restore Database</h2>
                            </div>
                            <p className="text-gray-700 mb-6 text-sm">Upload a previously generated backup JSON file to restore the database. <strong className="text-red-700">This action cannot be undone and will overwrite current data.</strong></p>

                            <div className="mb-4 relative">
                                <input
                                    id="backup-file-input"
                                    type="file"
                                    accept=".json"
                                    onChange={handleFileChange}
                                    className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2.5 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-red-100 file:text-red-700
                    hover:file:bg-red-200 border border-gray-300 rounded-lg cursor-pointer bg-white"
                                />
                            </div>

                            <button
                                onClick={handleRestore}
                                disabled={isRestoring || !selectedFile}
                                className={`w-full py-3 px-4 rounded-lg font-bold text-white shadow-md transition duration-200 flex justify-center items-center gap-2 ${(isRestoring || !selectedFile) ? "bg-red-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700 active:transform active:scale-95"
                                    }`}
                            >
                                {isRestoring ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Restoring Database...
                                    </>
                                ) : (
                                    "Restore Database"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DatabaseBackup;
